/**
 * Les tests A/B du site, et le tirage qui attribue a chaque visiteur sa
 * variante.
 *
 * Chaque test donne ses variantes et leur part, en pourcentage. Le visiteur
 * est tire au sort des son arrivee, pour tous les tests (le tag Umami de la
 * visite les porte, voir lib/umami), puis garde sa variante d'une visite a
 * l'autre : il ne voit pas A sur une partie et B sur sa revanche.
 *
 * Changer les parts d'un test refait le tirage de tout le monde a sa
 * prochaine lecture. Mettre une variante a 100 la montre a tous, et c'est
 * ainsi qu'on arrete un test ou qu'on garde son gagnant.
 */
export const AB_TESTS = {
    feedbackTest: { A: 50, B: 50 },
};

const AB_KEY = "dartsync_ab";

// Repli quand le stockage est indisponible (navigation privee, quota plein) :
// le tirage tient jusqu'au rechargement de la page. Une fois l'ecriture
// refusee, on ne relit plus le stockage, qui redonnerait un tirage perime.
let tiragesEnMemoire = {};
let stockageEnPanne = false;

const possede = (objet, cle) => Object.prototype.hasOwnProperty.call(objet, cle);

function signature(parts) {
    return Object.entries(parts)
        .map(([variante, part]) => `${variante}:${part}`)
        .join(",");
}

function tirer(parts, random) {
    const variantes = Object.entries(parts).filter(([, part]) => part > 0);
    if (variantes.length === 0) return Object.keys(parts)[0];
    const total = variantes.reduce((somme, [, part]) => somme + part, 0);
    let reste = random() * total;
    for (const [variante, part] of variantes) {
        reste -= part;
        if (reste < 0) return variante;
    }
    return variantes[variantes.length - 1][0];
}

function lireTirages() {
    try {
        const brut = JSON.parse(localStorage.getItem(AB_KEY) ?? "{}");
        return brut && typeof brut === "object" && !Array.isArray(brut) ? brut : {};
    } catch (erreur) {
        // Un contenu illisible repart de zero ; un stockage refuse passe en memoire.
        return erreur instanceof SyntaxError ? {} : null;
    }
}

function ecrireTirages(tirages) {
    try {
        localStorage.setItem(AB_KEY, JSON.stringify(tirages));
        return true;
    } catch {
        return false;
    }
}

// `?ab=feedbackTest:B` force une variante, plusieurs tests separes par des
// virgules. En developpement seulement : en production, une verification
// forcee partirait dans la mesure comme un affichage reel.
function varianteForcee(test, parts, search) {
    const valeur = new URLSearchParams(search).get("ab") ?? "";
    for (const morceau of valeur.split(",")) {
        const [nom, variante] = morceau.split(":");
        if (nom === test && possede(parts, variante)) return variante;
    }
    return null;
}

/**
 * Efface les tirages des tests retires du registre. Appelee au demarrage de
 * l'application : sans elle, un dernier test retire avec son ecran ne serait
 * plus jamais lu, et son tirage resterait chez le visiteur.
 */
export function purgeAbTests(tests = AB_TESTS) {
    const stockes = lireTirages();
    if (!stockes) return;
    const retires = Object.keys(stockes).filter((nom) => !possede(tests, nom));
    if (retires.length === 0) return;
    for (const nom of retires) delete stockes[nom];
    try {
        if (Object.keys(stockes).length === 0) localStorage.removeItem(AB_KEY);
        else localStorage.setItem(AB_KEY, JSON.stringify(stockes));
    } catch {
        // stockage indisponible : on reessaiera au prochain demarrage
    }
}

/** La variante du visiteur pour un test, tiree au premier appel puis conservee. */
export function abVariant(
    test,
    {
        search = "",
        allowOverride = process.env.NODE_ENV !== "production",
        random = Math.random,
        tests = AB_TESTS,
    } = {},
) {
    const parts = tests[test];
    if (!parts) return null;

    const forcee = allowOverride ? varianteForcee(test, parts, search) : null;
    if (forcee) return forcee;

    const stockes = stockageEnPanne ? null : lireTirages();
    const tirages = stockes ?? tiragesEnMemoire;
    let modifie = false;
    // Un test retire du registre n'a plus rien a faire chez le visiteur.
    for (const nom of Object.keys(tirages)) {
        if (!possede(tests, nom)) {
            delete tirages[nom];
            modifie = true;
        }
    }

    const sig = signature(parts);
    const actuel = tirages[test];
    if (!(actuel?.weights === sig && possede(parts, actuel.variant))) {
        tirages[test] = { variant: tirer(parts, random), weights: sig };
        modifie = true;
    }

    if (stockes && modifie && !ecrireTirages(tirages)) stockageEnPanne = true;
    if (!stockes || stockageEnPanne) tiragesEnMemoire = tirages;
    return tirages[test].variant;
}
